import AddonsPageTotal from 'app/components/AddonsPageTotal';
import CartCountdownTimer from 'app/components/CartCountdownTimer/CartCountdownTimer';
import CartSteps from 'app/components/CartSteps';
import CartStepCardSkeleton from 'app/components/Skeletons/Common/CartStepCardSkeleton';
import TopUpWallet from 'app/components/TopUpWallet';
import { Button } from 'app/components/ui/button';
import isEmpty from 'lodash/isEmpty';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { addAddonsAndTopUpsOnOrder } from 'store/cartSlice';
import {
  getEventDetailsLink,
  getEventIdFromSlug,
  getTicketsPageLink,
} from 'utils/events';
import { getDisplayedTickets } from 'utils/tickets';
import {
  selectCartData,
  selectWalletCredit,
  selectWhichChecksIfThereAreAddonsOnTheCartEvent,
} from '../../../store/cartSlice/selectors';
import {
  removeTopUpsAndUnbundledFromSelectedItems,
  setAllTheSelectedItems,
} from '../TicketSalePage/slice';
import {
  selectSelectedAddons,
  selectTicketIdsFromQueryParams,
} from '../TicketSalePage/slice/selectors';

const AddonsPage: React.FC<{}> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { eventSlug } = useParams();
  const eventId = getEventIdFromSlug(eventSlug);
  const {
    loading,
    selectedEvent: cartEvent,
    selectedEvent: { id, name, tickets: ticketsData, type },
    countdown: { onGoing },
    selectedItems: cartItems,
    uuid,
  } = useSelector(selectCartData);
  const walletData = useSelector(selectWalletCredit);
  const selectedAddons = useSelector(selectSelectedAddons);
  // const selectedItems = useSelector(selectSelectedItems);
  const methods = useForm();
  const { handleSubmit } = methods;
  const eventHasAddons = useSelector(
    selectWhichChecksIfThereAreAddonsOnTheCartEvent,
  );
  const pageShouldBeHidden = !eventHasAddons && isEmpty(walletData);

  const navigateToTheNextStep = useCallback(() => {
    let nextStepLink = `${getEventDetailsLink(
      name,
      id,
    )}/buy-tickets/billing-information`;

    if (searchParams.get('preview')) {
      nextStepLink += '?preview=true';
    }

    navigate(nextStepLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, name]);

  // useEmbedResize([selectedItems.length]);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/404');
      return;
    }
    if (pageShouldBeHidden) {
      navigateToTheNextStep();
      return;
    }
  }, [
    eventHasAddons,
    walletData,
    navigate,
    pageShouldBeHidden,
    cartItems.length,
    navigateToTheNextStep,
  ]);

  useEffect(() => {
    if (cartItems.length > 0 && id === eventId) {
      dispatch(setAllTheSelectedItems({ cartItems, cartEvent }));
    }
    return () => {
      dispatch(removeTopUpsAndUnbundledFromSelectedItems());
    };
  }, []);

  const submitForm = async data => {
    const sendData = await dispatch(
      addAddonsAndTopUpsOnOrder({
        cartUUID: uuid,
        eventId: id,
        addons: selectedAddons,
        topUps: data,
        tickets: displayedTickets,
        ticketsData,
        queryParams: {
          preview: searchParams.get('preview'),
        },
      }),
    );

    if (sendData.error?.code) {
      navigate('/cart/cancel', {
        state: {
          eventId: id,
          eventName: name,
          type,
        },
      });
      return;
    }

    navigateToTheNextStep();
  };

  const displayedTickets = getDisplayedTickets(cartEvent, cartItems);
  const ticketIdsFromQueryParams = useSelector(selectTicketIdsFromQueryParams);

  const previousPageLink = `${getTicketsPageLink(name, id, type)}${
    ticketIdsFromQueryParams ? `?tickets=${ticketIdsFromQueryParams}` : ''
  }${searchParams.get('preview') ? '?preview=true' : ''}`;

  return (
    <div className="page-container">
      <Helmet>
        <title>{t('cartSteps.pageTitle', { eventName: name })}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="flex justify-end px-4 mt-4">
        <CartCountdownTimer />
      </div>
      <div className="section-container">
        <CartSteps />
        {loading ? (
          <CartStepCardSkeleton />
        ) : (
          <div className="cart-step-card">
            <div className="cart-step-card-content">
              <div className="lg:mt-0 lg:mr-[16px] lg:w-2/3">
                <FormProvider {...methods}>
                  <form
                    id="payment-form"
                    onSubmit={handleSubmit(submitForm)}
                    noValidate
                  >
                    <h2 className="title-sm mb-3 sm:mb-6">
                      {t('topUpPage.sectionTitle')}
                    </h2>
                    <TopUpWallet
                      tickets={displayedTickets}
                      wallet={walletData}
                    />
                  </form>
                </FormProvider>
              </div>
              <AddonsPageTotal />
            </div>
            <div className="cart-step-card-footer">
              <Button variant="link" asChild>
                <Link
                  to={previousPageLink}
                  state={{ comesFromPreviousPage: true }}
                >
                  <ArrowLeft />
                  {t('common.back')}
                </Link>
              </Button>
              <Button disabled={!onGoing} type="submit" form="payment-form">
                {t('common.continue')}
                <ArrowRight />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddonsPage;
